import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { RocFormData } from '../../../../types/RocFormData'
import { RocFormField } from '../../../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Therapies subsection adapter
 * Handles therapy services and home exercise plans (5 fields)
 */
export class TherapiesAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Direct field extractions
    const therapies = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPIES
    )

    const therapyProvider = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPY_PROVIDER
    )?.at(0)

    const homeExercisePlan = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_PLAN
    )?.at(0)

    const homeExerciseDuration = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_DURATION
    )?.at(0)

    const homeExerciseFrequency = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_FREQUENCY
    )?.at(0)

    return {
      [RocFormField.THERAPIES_THERAPIES]: therapies,
      [RocFormField.THERAPIES_THERAPY_PROVIDER]: therapyProvider,
      [RocFormField.THERAPIES_HOME_EXERCISE_PLAN]: homeExercisePlan,
      [RocFormField.THERAPIES_HOME_EXERCISE_DURATION]: homeExerciseDuration,
      [RocFormField.THERAPIES_HOME_EXERCISE_FREQUENCY]: homeExerciseFrequency
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RocFormField.THERAPIES_THERAPIES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPIES,
        value: this.getFormDataField(RocFormField.THERAPIES_THERAPIES)
      })
    }

    if (this.formDataFieldExists(RocFormField.THERAPIES_THERAPY_PROVIDER)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPY_PROVIDER,
        value: this.getFormDataField(RocFormField.THERAPIES_THERAPY_PROVIDER)
      })
    }

    if (this.formDataFieldExists(RocFormField.THERAPIES_HOME_EXERCISE_PLAN)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_PLAN,
        value: this.getFormDataField(RocFormField.THERAPIES_HOME_EXERCISE_PLAN)
      })
    }

    if (this.formDataFieldExists(RocFormField.THERAPIES_HOME_EXERCISE_DURATION)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_DURATION,
        value: this.getFormDataField(RocFormField.THERAPIES_HOME_EXERCISE_DURATION)
      })
    }

    if (this.formDataFieldExists(RocFormField.THERAPIES_HOME_EXERCISE_FREQUENCY)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_FREQUENCY,
        value: this.getFormDataField(RocFormField.THERAPIES_HOME_EXERCISE_FREQUENCY)
      })
    }

    return mutations
  }
}
